import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setCurrentSector } from '../../../../actions/currentSectorActions';
import { setAllFarmDevices, setDashboardType, setSensorType } from '../../../../actions/farmSectorActions';
import CustomButton from '../../../../components/General/CustomButton';
import GoogleMaps from '../../../../components/Layout/Google/GoogleMaps';
import useNodes from '../../../../hooks/useNodes';
import useSensorModels from '../../../../hooks/useSensorModels';
import { NodeSchema, SensorModelSchema } from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import useStyles from '../styles';

type SensorType = 'Suelo' | 'Clima' | 'Planta' | 'Riego';

const getSectorsBySensorTypes = (
  selectedType: string,
  sectors: Array<any> = [],
  devices: Array<NodeSchema | SensorModelSchema> = [],
): Array<any> => {
  // If all types are selected, return all sectors
  if (selectedType === 'todos') {
    return sectors;
  }

  // If no types selected, return empty array
  if (selectedType === '') {
    return [];
  }

  // Return sectors that have at least one sensor of selected types
  return sectors.filter((sector) => {
    const sectorDevices = devices.filter(
      (device) => device?.sector?.id === sector.id
        && !('nodeType' in device)
        && device?.sensorType === selectedType,
    );
    return sectorDevices.length > 0;
  });
};

const getSectorSensorTypes = (sectorId: string,
  devices: Array<NodeSchema | SensorModelSchema> = []) => {
  const sectorDevices = devices.filter(
    (device) => device?.sector?.id === sectorId && !('nodeType' in device),
  );
  return {
    suelo: sectorDevices.some((device) => (device as SensorModelSchema)?.sensorType === 'Suelo'),
    clima: sectorDevices.some((device) => (device as SensorModelSchema)?.sensorType === 'Clima'),
    riego: sectorDevices.some((device) => (device as SensorModelSchema)?.sensorType === 'Riego'),
    planta: sectorDevices.some((device) => (device as SensorModelSchema)?.sensorType === 'Planta'),
  };
};

const AccountHomeMap = () => {
  const classes = useStyles();
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);
  const dispatch = useDispatch();
  const [selectedSensorType, setSelectedSensorType] = useState<'suelo' | 'clima' | 'planta' | 'riego' | 'todos'>('todos');
  const { nodes } = useNodes(selectedFarm?.id ?? '');
  const { sensorModels } = useSensorModels(selectedFarm?.id ?? '');
  const allFarmDevices = useMemo(
    () => {
      dispatch(setAllFarmDevices([...nodes, ...sensorModels]));
      return [...nodes, ...sensorModels];
    },
    [nodes, sensorModels],
  );

  const history = useHistory();

  const handleSectorClick = (sectorId: string) => {
    if (selectedSector?.id === sectorId) {
      dispatch(setCurrentSector(null));
    } else {
      // Find the sector object in the selectedFarm
      const selectedSectorObj = selectedFarm?.sectors?.find((sector) => sector.id === sectorId);

      if (selectedSectorObj) {
        dispatch(setCurrentSector(selectedSectorObj)); // Set current sector in Redux
      }
    }
  };

  // A lo mejor solo nos querremos mostrar los sectores que tengan un determinado tipo de sensor
  const polygonsAux = getSectorsBySensorTypes(
    selectedSensorType,
    selectedFarm?.sectors,
    allFarmDevices,
  ).map((sector) => ({
    id: sector.id,
    coordinates: sector?.coordinates ?? [],
    isSelected: selectedSector?.id === sector.id,
    onPolygonClick: () => handleSectorClick(sector.id ?? ''),
  }));

  const farmCoordinates = selectedFarm?.coordinates
    ? selectedFarm.coordinates.map(({ lat: farmLat, lng: farmLng }) => ({
      lat: typeof farmLat === 'string' ? parseFloat(farmLat) : farmLat,
      lng: typeof farmLng === 'string' ? parseFloat(farmLng) : farmLng,
    })) : null;

  const selectedSectorInfo = selectedFarm?.sectors?.find(
    (sector) => sector.id === selectedSector?.id,
  );

  const SENSOR_TYPES: { key: SensorType; label: string }[] = [
    { key: 'Suelo', label: 'Suelo' },
    { key: 'Clima', label: 'Clima' },
    { key: 'Planta', label: 'Planta' },
    { key: 'Riego', label: 'Riego' },
  ];

  const handleClick = (type: SensorType) => {
    dispatch(setSensorType(type));
    const dashboardType = type === 'Suelo' ? 1 : 2; // 1 para suelo, 2 para planta
    dispatch(setDashboardType(dashboardType));
    history.push(`/sectores/${selectedSector?.id}?tab=1&type=${type}`
      + `&farmId=${selectedFarm?.id}`);
  };

  const getSensorTypeButtons = () => {
    if (!selectedSector?.id || !allFarmDevices) return null;

    const sensorTypes = getSectorSensorTypes(selectedSector.id, allFarmDevices);

    const activeTypes = SENSOR_TYPES.filter((type) => sensorTypes[type.key as keyof typeof sensorTypes] && (type.key === 'Suelo' || type.key === 'Planta'));

    const firstRow = activeTypes.slice(0, 2);
    const secondRow = activeTypes.slice(2);

    localStorage.setItem('sector-tab', '1');

    return activeTypes.length > 0 ? (
      <Box>
        <Box display='flex' gap='10px' justifyContent='center' mb={1}>
          {firstRow.map((type) => (
            <CustomButton
              key={type.key}
              buttonText={type.label}
              onClick={() => handleClick(type.key)}
              color="blue"
            />
          ))}
        </Box>
        {secondRow.length > 0 && (
          <Box display='flex' gap='10px' justifyContent='center'>
            {secondRow.map((type) => (
              <CustomButton
                key={type.key}
                buttonText={type.label}
                onClick={() => handleClick(type.key)}
                color="blue"
              />
            ))}
          </Box>
        )}
      </Box>
    ) : null;
  };

  return (
    <Card className={classes.cardLayoutContainer}>
      <CardContent className={classes.cardLayoutContentContainer}>
        {!selectedFarm ? (
          <>
            <Typography
              variant='h3'
              color="primary"
              gutterBottom
              display="inline"
            >
              Mapa
            </Typography>
            <Divider className={classes.divider} />
            <Typography className={classes.noFarmSelectedText}>
              Elige un campo para ver el mapa
            </Typography>
          </>
        ) : (
          <>
            <Box className={classes.cardHeaderLayoutContentContainer}>
              <Box display='flex' justifyContent='space-between' alignItems='center' width='100%'>
                <Typography
                  variant='h3'
                  color="primary"
                  gutterBottom
                  display="inline"
                >
                  Mapa
                </Typography>
                <Box>
                  <Chip
                    key='todos'
                    color={selectedSensorType === 'todos' ? 'primary' : undefined}
                    label="Todos"
                    onClick={() => setSelectedSensorType('todos')}
                    size='small'
                    className={classes.layoutMapChip}
                  />
                </Box>
              </Box>
            </Box>
            <Box className={classes.googleMapsContainer} style={{ position: 'relative', padding: '0' }}>
              <GoogleMaps
                lat={selectedFarm?.center?.lat}
                lng={selectedFarm?.center?.lng}
                drawable='none'
                farm={{
                  id: selectedFarm?.id ?? '',
                  coordinates: farmCoordinates,
                  zoom: selectedFarm?.zoom ?? 18,
                }}
                polygons={polygonsAux}
                sectorInfo={selectedSectorInfo ? {
                  data: {
                    Nombre: `${selectedSectorInfo?.name || '-'}`,
                    Especie: `${selectedSectorInfo?.variety?.species || '-'}`,
                    Variedad: `${selectedSectorInfo?.variety?.name || '-'}`,
                    'Año de plantación': `${selectedSectorInfo?.plantingYear || '-'}`,
                    Hectáreas: `${selectedSectorInfo?.theoreticalSurface || '-'} HA`,
                  },
                  customButton: getSensorTypeButtons(),
                } : null}
              />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AccountHomeMap;
